"use client";
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import { useEnvContext } from "next-runtime-env";

import SuccessSvg from "assets/svg/success.svg";

import { forgotPassword } from "@/src/services/auth";

import validate from "helpers/form-validation";
import getMessage from "helpers/error-messages";

import Input from "components/common/input";
import Button from "components/common/button";
import Loading from "components/common/loading-spinner";
import InitialPageLoadGtmEvent from "components/gtm-load-events/initialPageLoadEvents";

import styles from "./styles.module.css";

const ForgotPasswordForm = ({ modal, handleBackButtonForgotForm }) => {
  const recaptchaRef = useRef();
  const againRecaptchaRef = useRef();
  const [formEmail, setFormEmail] = useState("");
  const [errors, setErrors] = useState({
    email: {
      value: null,
      message: null,
    },
    wrongUser: {
      value: null,
      message: null,
    },
  });
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(false);
  const { NEXT_PUBLIC_EJ_KEYS_RECAPTCHA } = useEnvContext();

  let userMail = "";
  if (typeof window !== "undefined") {
    userMail = localStorage.getItem("forgot-password-user");
  }

  useEffect(() => {
    if (userMail !== "") {
      setFormEmail(userMail);
    }
  }, []);

  const handleBackButton = () => {
    if (modal) {
      handleBackButtonForgotForm();
    } else {
      window.location.href = "/giris";
    }
  };

  const handleInputOnChange = (value, type) => {
    setFormEmail(value);
  };

  const handleInputFocus = (e, type) => {
    setErrors({ ...errors, [type]: { value: false, message: null } });
  };

  const handleClick = async (againLink) => {
    const hasError = await validateForm();
    if (hasError) return null;
    let gRecaptchaResponse;
    if (!againLink) {
      const response = await recaptchaRef.current.execute();
      gRecaptchaResponse = response;
    } else {
      const response = await againRecaptchaRef.current.execute();
      gRecaptchaResponse = response;
    }

    const params = {
      email: formEmail,
      gRecaptchaResponse,
    };
    try {
      setLoading(true);
      const response = await forgotPassword(params);
      if (response?.status === "error") {
        const message = getMessage(response);
        const { code } = message;
        if (code === "4000025") {
          setErrors({
            ...errors,
            email: {
              value: true,
              message: message?.message,
            },
          });
          setLoading(false);
          return;
        }
      }
      localStorage.removeItem("forgot-password-user");
      setLoading(false);
      setInfo(true);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const validateForm = async () => {
    const fieldErrors = await validate({
      email: formEmail === "" ? null : formEmail,
    });
    const errorEmail = fieldErrors?.email;

    setErrors({
      ...errors,
      email: {
        value: !!errorEmail,
        message: errorEmail,
      },
    });

    return !!errorEmail;
  };

  const handleKeyPress = (e) => {
    if (e.code === "Enter") {
      handleClick(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <InitialPageLoadGtmEvent pageNameForSearchId="forgotpassword" pageType="sifremi-unuttum" />
      {info ? (
        <div className={styles.successWrapper}>
          <div className={styles.icon}>
            <SuccessSvg />
          </div>
          <p className={styles.title}>E-postanı Kontrol Et!</p>
          <p className={styles.info}>
            Şifre yenileme bağlantısı e-posta adresine gönderilmiştir, lütfen e-posta adresini kontrol et.
          </p>
          <div className={styles.signUp}>
            <p>Mail Gelmedi mi?</p>
            <span>
              <button
                className={styles.backLink}
                onClick={() => {
                  handleClick(true);
                }}
              >
                {loading ? <Loading loadingClass={styles.loading} /> : "Tekrar Gönder"}
              </button>
            </span>
          </div>
          <ReCAPTCHA
            key="captcha"
            ref={againRecaptchaRef}
            sitekey={window.env.EJ_KEYS_RECAPTCHA}
            size="invisible"
            badge="inline"
            className={styles.againRecaptcha}
          />
        </div>
      ) : (
        <div className={styles.formWrapper}>
          <h3 className={styles.title}>Şifremi Unuttum</h3>
          <p className={styles.info}>
            Üzülme, herkesin başına gelir! Şifreni yenilemek için üye olduğun e-posta adresini yazman yeterli.
          </p>
          <div>
            <span className={styles.inputTitle}>E-posta Adresi</span>
            <div className={styles.mailInput}>
              <Input
                errorMessage={errors?.email?.message}
                inputClass={styles.inputClass}
                value={formEmail}
                onChange={(e) => handleInputOnChange(e?.target?.value, "email")}
                onFocus={(e) => handleInputFocus(e?.target?.value, "email")}
                onKeyPress={handleKeyPress}
                placeholder="E-posta Adresinizi Giriniz"
              />
            </div>
            <div className={styles.reCaptcha}>
              <ReCAPTCHA
                key="captcha"
                ref={recaptchaRef}
                sitekey={NEXT_PUBLIC_EJ_KEYS_RECAPTCHA}
                size="invisible"
                badge="inline"
              />
            </div>
          </div>
          <div className={styles.loginButton}>
            <Button size="M" isLoading={loading} onClick={() => handleClick(false)}>
              Şifremi Yenile
            </Button>
          </div>
          <div className={styles.signUp}>
            <span>
              <button onClick={handleBackButton} className={styles.backLink}>
                Geri
              </button>{" "}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

ForgotPasswordForm.propTypes = {
  handleBackButtonForgotForm: PropTypes.func,
  modal: PropTypes.bool,
};

export default ForgotPasswordForm;
